import { measurementString } from "components/util/measurementString";
import { fabric } from "fabric";

const DEBUG = false;

export const UNSELECTABLE_PROPERTIES = DEBUG
    ? {
          controls: {},
          selectable: false,
          hasBorders: true,
          borderColor: "red",
          cornerColor: "red",
          borderOpacityWhenMoving: 1,
          stroke: "red",
          strokeWidth: 2,
      }
    : {
          controls: {},
          hasBorders: false,
          selectable: false,
      };

const NODE_SELECT_PROPERTIES = {
    lockMovementY: true,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
    hasControls: false,
    hasBorders: false,
    selectable: true,
    hoverCursor: "grab",
    moveCursor: "grabbing",
};

class Ruler {
    static width = 4;
    static transparentColour = "rgba(255,0,0,0.5)";
    static solidColour = "#ff0000";
    static nodeRadius = Ruler.width + 2;

    fabricCanvas = null;
    scaleFactorRef = null;
    setMeasureActive = () => {};
    uncompleteRulerRef = null;

    startNode = null;
    endNode = null;
    body = null;
    text = null;

    grabOffsetX = null;
    grabOffsetY = null;

    visible = false;
    left = 0;
    top = 0;
    length = 0;
    imageHeightLimit = null;

    constructor(fabricCanvas, scaleFactorRef, setMeasureActive, uncompleteRulerRef, imageHeightLimit) {
        this.fabricCanvas = fabricCanvas;
        this.scaleFactorRef = scaleFactorRef;
        this.setMeasureActive = setMeasureActive;
        this.uncompleteRulerRef = uncompleteRulerRef;
        this.imageHeightLimit = imageHeightLimit;

        this.init();
    }

    init() {
        this.startNode = this.generateNode(0);
        this.fabricCanvas.add(this.startNode);

        this.fabricCanvas.on("mouse:move", this.handleInitialMove);
        this.fabricCanvas.on("mouse:down", this.handleInitialClick);
    }

    handleInitialMove = (event) => {
        const pointerPosition = this.fabricCanvas.getPointer(event.e);
        this.top =
            pointerPosition.y < this.imageHeightLimit
                ? pointerPosition.y - 0.5 * (Ruler.nodeRadius + Ruler.width)
                : this.imageHeightLimit - 0.5 * (Ruler.nodeRadius + Ruler.width);

        this.startNode.set({
            left: pointerPosition.x - Ruler.nodeRadius,
            top: this.top,
        });

        this.fabricCanvas.requestRenderAll();
    };

    handleInitialClick = (event) => {
        this.fabricCanvas.off("mouse:move", this.handleInitialMove);
        this.fabricCanvas.off("mouse:down", this.handleInitialClick);

        const pointerPosition = this.fabricCanvas.getPointer(event.e);
        this.endNode = this.generateNode(pointerPosition.x);
        this.fabricCanvas.add(this.endNode);

        this.body = this.generateBody();
        this.fabricCanvas.add(this.body);

        this.text = this.generateText();
        this.fabricCanvas.add(this.text);

        this.fabricCanvas.on("mouse:move", this.handleEndNodeMove);
        this.fabricCanvas.on("mouse:down", this.handleEndNodeClick);

        if (this.uncompleteRulerRef.current) this.uncompleteRulerRef.current = null;
    };

    handleEndNodeMove = (event) => {
        const pointerPosition = this.fabricCanvas.getPointer(event.e);

        this.endNode.set({ left: pointerPosition.x - Ruler.nodeRadius });
        this.handleNodeMove(false);
        this.fabricCanvas.requestRenderAll();
    };

    handleEndNodeClick = () => {
        this.fabricCanvas.off("mouse:move", this.handleEndNodeMove);
        this.fabricCanvas.off("mouse:down", this.handleEndNodeClick);
        this.setMeasureActive(false);
        this.attachNodeMoveListeners();
        this.attachBodyMoveListener();
    };

    setImageHeightLimit(heightLimit) {
        this.imageHeightLimit = heightLimit;
    }

    generateBody() {
        const nodeCenterY = this.top + Ruler.nodeRadius - 0.25 * Ruler.width;

        return new fabric.Line([this.startNode.left + Ruler.nodeRadius, nodeCenterY, this.endNode.left + Ruler.nodeRadius, nodeCenterY], {
            stroke: Ruler.solidColour,
            strokeWidth: Ruler.width,
            zIndex: 100,
            id: "ruler_body",
            ...UNSELECTABLE_PROPERTIES,
            selectable: true,
            hoverCursor: "grab",
            moveCursor: "grabbing",
        });
    }

    generateNode(left) {
        const top = this.top;

        return new fabric.Circle({
            ...NODE_SELECT_PROPERTIES,
            radius: Ruler.nodeRadius,
            fill: Ruler.transparentColour,
            strokeWidth: 2,
            stroke: Ruler.solidColour,
            left: left - Ruler.nodeRadius,
            top,
            zIndex: 100,
            id: !this.startNode ? "ruler_left" : "ruler_right",
        });
    }

    handleNodeMove(isLeft = true) {
        const startX = this.startNode.left + Ruler.nodeRadius;
        const endX = this.endNode.left + Ruler.nodeRadius;

        this.body.set({
            x1: startX,
            x2: endX,
            y1: this.startNode.top + Ruler.nodeRadius - 0.25 * Ruler.width,
            y2: this.endNode.top + Ruler.nodeRadius - 0.25 * Ruler.width,
        });

        this.length = endX - startX;

        if (this.text) this.text.text = measurementString(Math.abs(this.length * this.scaleFactorRef.current));

        this.updateTextPosition();
        this.fabricCanvas.requestRenderAll();
    }

    disableCanvasInteractions = () => {
        this.fabricCanvas.isDragging = false;
        this.fabricCanvas.on("mouse:down", this.preventPanning);
    };

    enableCanvasInteractions = () => {
        this.fabricCanvas.off("mouse:down", this.preventPanning);

        this.fabricCanvas.on("mouse:down", (event) => {
            this.fabricCanvas.isDragging = true;
            this.fabricCanvas.lastPosX = event.e.clientX;
            this.fabricCanvas.lastPosY = event.e.clientY;
        });

        this.fabricCanvas.on("mouse:move", (event) => {
            if (this.fabricCanvas.isDragging) {
                const e = event.e;
                const vpt = this.fabricCanvas.viewportTransform;

                vpt[4] += e.clientX - this.fabricCanvas.lastPosX;
                vpt[5] += e.clientY - this.fabricCanvas.lastPosY;

                this.fabricCanvas.lastPosX = e.clientX;
                this.fabricCanvas.lastPosY = e.clientY;

                this.fabricCanvas.requestRenderAll();
            }
        });

        this.fabricCanvas.on("mouse:up", () => {
            this.fabricCanvas.isDragging = false;
        });
    };

    attachNodeMoveListeners() {
        const handleMove = (isLeft) => {
            this.handleNodeMove(isLeft);
        };

        const onNodeMoveStart = () => {
            this.disableCanvasInteractions();
        };

        const onNodeMoveEnd = () => {
            this.enableCanvasInteractions();
        };

        this.startNode.on("mousedown", onNodeMoveStart);
        this.endNode.on("mousedown", onNodeMoveStart);

        this.startNode.on("moving", () => {
            handleMove(true);
        });
        this.endNode.on("moving", () => {
            handleMove(false);
        });

        this.startNode.on("moved", onNodeMoveEnd);
        this.endNode.on("moved", onNodeMoveEnd);
    }

    attachBodyMoveListener() {
        this.body.on("mousedown", (event) => {
            this.disableCanvasInteractions();
            const pointerPosition = this.fabricCanvas.getPointer(event.e);

            this.grabOffsetX = pointerPosition.x - this.body.x1;
            this.grabOffsetY = pointerPosition.y - this.body.y1;

            this.fabricCanvas.on("mouse:move", this.handleBodyMove);
            this.fabricCanvas.on("mouse:up", this.handleBodyMoveEnd);
        });
    }

    handleBodyMove = (event) => {
        const pointerPosition = this.fabricCanvas.getPointer(event.e);
        const newStartX = pointerPosition.x - this.grabOffsetX;
        const deltaX = newStartX - (this.startNode.left + Ruler.nodeRadius);

        const newStartY = pointerPosition.y - this.grabOffsetY;
        const deltaY = newStartY - (this.startNode.top + Ruler.nodeRadius);

        if (this.startNode.top + deltaY >= this.imageHeightLimit - 2 * Ruler.nodeRadius || this.startNode.top + deltaY < 0) {
            this.handleNodeMove();
            return;
        }

        this.startNode.set({
            left: this.startNode.left + deltaX,
            top: this.startNode.top + deltaY,
        });
        this.endNode.set({
            left: this.endNode.left + deltaX,
            top: this.endNode.top + deltaY,
        });
        this.top += deltaY;
        this.handleNodeMove();

        this.fabricCanvas.requestRenderAll();
    };

    handleBodyMoveEnd = () => {
        this.fabricCanvas.off("mouse:move", this.handleBodyMove);
        this.fabricCanvas.off("mouse:up", this.handleBodyMoveEnd);
        this.enableCanvasInteractions();
    };

    updateTextPosition() {
        if (!this.text) return;

        this.text.set({
            left: this.body.x1 + 0.5 * (this.length - this.text.width),
            top: this.top + 8,
        });
    }

    generateText() {
        const centerX = (this.startNode.left + this.endNode.left) / 2 + Ruler.nodeRadius;
        const distance = Math.abs(this.length * this.scaleFactorRef.current);

        const text = new fabric.Text(measurementString(distance), {
            fill: "white",
            top: this.top + 8,
            fontFamily: "Barlow",
            fontSize: 28,
            id: "ruler_text",
            ...UNSELECTABLE_PROPERTIES,
        });

        text.set({ left: centerX - text.width / 2 });
        return text;
    }

    cleanup = () => {
        this.getElements().forEach((element) => {
            if (!element) return;

            if (element.off) {
                element.off();
            }

            this.fabricCanvas.remove(element);

            element = null;
        });

        this.fabricCanvas.renderAll();

        if ((this.uncompleteRulerRef = this)) {
            this.fabricCanvas.off("mouse:move", this.handleInitialMove);
            this.fabricCanvas.off("mouse:down", this.handleInitialClick);
        }
    };

    bringToFront() {
        this.fabricCanvas.bringToFront(this.body);
        this.fabricCanvas.bringToFront(this.startNode);
        this.fabricCanvas.bringToFront(this.endNode);
        this.fabricCanvas.bringToFront(this.text);
    }

    getElements() {
        return [this.body, this.startNode, this.endNode, this.text];
    }
}

export default Ruler;
